import { Injectable } from '@angular/core';
import { DialogContainerService, DialogRef, DialogService, DialogSettings } from '@progress/kendo-angular-dialog';

/**
 * Keeps track of the dialogs opened through `DialogService` so they can be closed together.
 * Kendo mounts these dialogs in the app-level `kendoDialogContainer`, so they outlive route changes
 * (e.g. the browser Back/Forward buttons) unless something closes them.
 */
@Injectable({ providedIn: 'root' })
export class OpenDialogRegistry {
    private readonly openDialogs = new Set<DialogRef>();

    add(dialogRef: DialogRef): void {
        this.openDialogs.add(dialogRef);
        const forget = () => this.openDialogs.delete(dialogRef);
        dialogRef.result.subscribe({ next: forget, error: forget, complete: forget });
    }

    closeAll(): void {
        [...this.openDialogs].forEach((dialogRef) => dialogRef.close());
    }
}

/**
 * Drop-in `DialogService` that registers every dialog in the `OpenDialogRegistry`.
 * Provided in `CommonModule` so it replaces the instance that `DialogModule` provides in every module.
 */
@Injectable()
export class TrackedDialogService extends DialogService {
    // Kendo 25's DialogService takes only the container service (ComponentFactoryResolver is gone)
    constructor(
        containerService: DialogContainerService,
        private readonly registry: OpenDialogRegistry
    ) {
        super(containerService);
    }

    override open(settings: DialogSettings): DialogRef {
        const dialogRef = super.open(settings);
        this.registry.add(dialogRef);
        return dialogRef;
    }
}
